import { useMemo } from "react";
import { SecondaryColor } from "../../../../common";
import { BrushStart, LegendKey } from "../../../../models/chart_model";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { LARGE_DATA_THRESHOLD } from "../constants";
import { formatAxisValue } from "../utils/formatUtils";
import { buildSecondXAxisConfig } from "./buildSecondXAxisConfig";
import { EChartsSeries } from "../types";

/**
 * Inputs for buildOptions. Grouped by concern below for readability.
 */
interface Params {
  // ---- data ----
  /** Memoized chart data rows. */
  debouncedData: any[];
  /** X-axis values pre-extracted from `debouncedData[xDataKey]`. */
  xAxisData: any[];
  /** Field on each row that drives the primary x-axis. */
  xDataKey: string;
  /** Optional second-axis date key (paints date row under primary x). */
  secondXDataKey?: string;

  // ---- legends + colors ----
  /** Legends sorted via compareLegends, filtered by selection. */
  orderedSelectedLegends: LegendKey[];
  /** Active selection state from the user. */
  selectedLegends: LegendKey[];
  /** Names hidden from rendering (e.g. summary-only metrics). */
  hiddenStats: string[];
  /** Stable color per `getLegendColorKey`. */
  colorMap: Record<string, string>;
  /** Per-bar palette when `colorByPoint` is on. */
  barColors?: string[];
  /** When true, bars draw their per-point color from `barColors`. */
  colorByPoint?: boolean;

  // ---- bar tuning ----
  /** Category gap between bars in percent. */
  barCategoryGap: number;
  /** True when chart belongs to the price-forecast view (bar coloring rule). */
  isPriceForecast: boolean;

  // ---- brush / zoom ----
  brushStart: BrushStart;
  showBrush: boolean;
  brushHeight: number;
  brushIndex?: { startIndex?: number; endIndex?: number };
  enableMouseWheelZoom: boolean;
  /** Reactive zoom from useZoomState — drives 2nd x-axis label density. */
  zoomRange: { start: number; end: number } | null;
  /** Last zoom captured into ref — preserves zoom across legend toggles. */
  lastZoomRef: React.MutableRefObject<{ start: number; end: number } | null>;

  // ---- layout ----
  left?: number;
  bottom: number;
  top: number;
  xaxisHeight: number;
  isSmallLegend: boolean;

  // ---- tooltip ----
  customTooltip?: any;
  tooltipFormatter: any;

  // ---- text / fonts ----
  fontSize: number;
  labelFontSize?: number;
  xLabel?: string;
  yAxisLabel?: string;
  secondYAxisLabel?: string;

  // ---- axes ----
  xAxisAngle?: number;
  xAxisTickMargin: number;
  xAxisLabelPosition: any;
  xAxisLabelOffset?: number;
  xAxisInterval?: number | "auto";
  yMaxValue?: number;
  nameGap?: number;
  secondYAxisDomain?: number[];

  // ---- misc ----
  showToolbox: boolean;
  /** Pulled into series builder via callback. */
  buildSeries: any;
  /** Enable invisible click-capture overlay. */
  enableClickSelection?: boolean;
  /** X-values to highlight via translucent block overlay. */
  highlightedXValues?: any[];
  /** Time-slot mode flips smart-interval behaviour to "auto". */
  isTimeSlot?: boolean;
}

export function useChartOptions(p: Params) {
  const {
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    brushStart,
    left,
    showBrush,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    zoomRange,
    lastZoomRef,
  } = p;

  return useMemo(() => {
    if (!debouncedData || debouncedData.length === 0) return {};

    const isLarge = debouncedData.length > LARGE_DATA_THRESHOLD;

    const series: EChartsSeries[] = buildSeries(
      debouncedData,
      orderedSelectedLegends,
      selectedLegends,
      hiddenStats,
      colorMap,
      barCategoryGap,
      isPriceForecast,
      colorByPoint,
      barColors,
    );

    // Compute maxValue once for both click & highlight layers
    let cachedMaxValue: number | null = null;
    const getMaxValue = () => {
      if (cachedMaxValue !== null) return cachedMaxValue;
      let max = 1000;
      const activeLegends = orderedSelectedLegends.filter(
        (leg) => !hiddenStats.includes(leg.name || ""),
      );
      // Sample at most 500 points for max calculation on large datasets
      const step = isLarge
        ? Math.max(1, Math.floor(debouncedData.length / 500))
        : 1;
      for (let i = 0; i < debouncedData.length; i += step) {
        for (const leg of activeLegends) {
          const v = Number(debouncedData[i][leg.dataKey]);
          if (!isNaN(v) && v > max) max = v;
        }
      }
      cachedMaxValue = max;
      return max;
    };

    if (enableClickSelection && debouncedData.length > 0) {
      const maxValue = getMaxValue();
      series.push({
        name: "__click_layer__",
        type: "bar",
        barGap: "-100%",
        data: debouncedData.map(() => maxValue * 1.2),
        itemStyle: { color: "transparent" },
        z: 100,
        cursor: "pointer",
        yAxisIndex: 0,
        legendHoverLink: false,
        animation: false,
      } as any);
    }

    if (
      highlightedXValues &&
      highlightedXValues.length > 0 &&
      debouncedData.length > 0
    ) {
      const maxValue = getMaxValue();
      const highlightSet = new Set(highlightedXValues.map(String));
      series.push({
        name: "__highlight_layer__",
        type: "bar",
        barGap: "-100%",
        data: debouncedData.map((item) =>
          highlightSet.has(String(item[xDataKey])) ? maxValue * 1.2 : 0,
        ),
        itemStyle: { color: "rgba(238, 244, 254, 0.75)" },
        z: 10,
        silent: true,
        yAxisIndex: 0,
        legendHoverLink: false,
        animation: false,
      } as any);
    }

    const brushStartIndex = Math.max(0, debouncedData.length - 96);
    const brushEndIndex = Math.max(0, debouncedData.length - 1);

    const safeLen = debouncedData.length || 1;
    const defaultStartPercent =
      brushIndex?.startIndex !== undefined
        ? (brushIndex.startIndex / safeLen) * 100
        : (brushStartIndex / safeLen) * 100;
    const defaultEndPercent =
      brushIndex?.endIndex !== undefined
        ? ((brushIndex.endIndex + 1) / safeLen) * 100
        : ((brushEndIndex + 1) / safeLen) * 100;

    const zoomStart =
      zoomRange?.start ?? lastZoomRef.current?.start ?? defaultStartPercent;
    const zoomEnd =
      zoomRange?.end ?? lastZoomRef.current?.end ?? defaultEndPercent;

    const dataZoomXAxisIndex = secondXDataKey ? [0, 1] : [0];

    const visibleFraction = Math.max(0.01, (zoomEnd - zoomStart) / 100);
    const visibleDataCount = Math.ceil(debouncedData.length * visibleFraction);

    const computeSmartXInterval = (): number | "auto" => {
      if (xAxisInterval !== undefined) return xAxisInterval;
      if (isTimeSlot) return "auto";
      if (visibleDataCount <= 15) return "auto";
      if (visibleDataCount <= 30) return Math.floor(visibleDataCount / 8) - 1;
      if (visibleDataCount <= 60) return Math.floor(visibleDataCount / 8) - 1;
      if (visibleDataCount <= 90) return Math.floor(visibleDataCount / 10) - 1;
      if (visibleDataCount <= 180) return Math.floor(visibleDataCount / 12) - 1;
      if (visibleDataCount <= 270) return Math.floor(visibleDataCount / 12) - 1;
      if (visibleDataCount <= 365) return Math.floor(visibleDataCount / 12) - 1;
      if (visibleDataCount <= 450) return Math.floor(visibleDataCount / 12) - 1;
      return Math.floor(visibleDataCount / 12) - 1;
    };

    const smartInterval = computeSmartXInterval();
    const lastXIndex = Math.max(0, xAxisData.length - 1);
    const xAxisIntervalFn =
      typeof smartInterval === "number"
        ? (index: number) => {
            if (index === 0 || index === lastXIndex) return true;
            return index % (smartInterval + 1) === 0;
          }
        : smartInterval;

    const secondXAxisConfig = secondXDataKey
      ? buildSecondXAxisConfig({
          debouncedData,
          secondXDataKey,
          zoomStart,
          zoomEnd,
          fontSize,
          labelFontSize,
        })
      : [];

    return {
      animation: !isLarge,
      ...(isLarge
        ? { progressive: 3000, progressiveThreshold: 5000 }
        : {}),
      grid: {
        left: left ?? 40,
        right: 50,
        bottom: showBrush
          ? brushHeight + xaxisHeight - (isSmallLegend ? 30 : 10)
          : xaxisHeight - bottom,
        top: top || 20,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          snap: true,
          label: { show: false },
          xAxisIndex: 0,
          animation: false,
        },
        formatter: customTooltip || tooltipFormatter,
        enterable: true,
        confine: true,
        backgroundColor: "#fff",
        borderRadius: 10,
        textStyle: { fontSize, fontWeight: 500, color: "#333" },
        extraCssText:
          "box-shadow: 0px 0px 10px 0px rgba(0,0,0,0.1);border:none;max-height:calc(100vh - 350px);overflow-y:auto;padding-right:10px",
        showDelay:
          visibleDataCount > 2000
            ? 80
            : visibleDataCount > 500
              ? 40
              : visibleDataCount > 200
                ? 15
                : 0,
        transitionDuration: 0.4,
      },
      legend: {
        show: false,
        data: series
          .filter((s: any) => !String(s.name).startsWith("__"))
          .map((s: any) => s.name),
      },
      toolbox: {
        show: showToolbox ?? true,
        top: -5,
        feature: {
          dataZoom: { yAxisIndex: "none" },
          restore: {},
          saveAsImage: {},
        },
      },
      xAxis: [
        {
          type: "category",
          data: xAxisData,
          axisLabel: {
            fontSize,
            rotate: xAxisAngle || 0,
            margin: xAxisTickMargin + 4,
            color: "#8C8A96",
            interval: xAxisIntervalFn,
            formatter: (value: string) =>
              typeof value === "string" ? normalizeDisplayLabel(value) : value,
            hideOverlap: false,
            showMinLabel: true,
            showMaxLabel: true,
          },
          axisLine: { lineStyle: { color: "#E0E0E0" } },
          axisTick: {
            show: true,
            alignWithLabel: true,
            length: 4,
            lineStyle: { color: "#E0E0E0" },
          },
          name: xLabel,
          nameLocation:
            xAxisLabelPosition === "insideBottom"
              ? "middle"
              : xAxisLabelPosition,
          nameGap: xAxisLabelOffset || 15,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize + 3,
            color: SecondaryColor,
          },
        },
        ...secondXAxisConfig,
      ],
      yAxis: [
        {
          type: "value",
          ...(yMaxValue ? { max: yMaxValue } : {}),
          splitNumber: 5,
          axisLabel: {
            fontSize,
            margin: 8,
            formatter: formatAxisValue,
            hideOverlap: true,
          },
          axisTick: { show: true, length: 4, lineStyle: { color: "#E0E0E0" } },
          scale: false,
          boundaryGap: [0, 0.1],
          name: yAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        {
          type: "value",
          position: "right",
          ...(secondYAxisDomain
            ? { min: secondYAxisDomain[0], max: secondYAxisDomain[1] }
            : {}),
          alignTicks: true,
          splitNumber: 5,
          axisLabel: {
            fontSize,
            margin: 8,
            formatter: formatAxisValue,
            hideOverlap: true,
          },
          axisTick: { show: true, length: 4, lineStyle: { color: "#E0E0E0" } },
          scale: false,
          boundaryGap: [0, 0.1],
          name: secondYAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
      ],
      series,
      dataZoom: [
        ...(showBrush
          ? [
              {
                type: "slider",
                start: zoomStart,
                end: zoomEnd,
                height: brushHeight,
                width: "98%",
                bottom: 10,
                left: 10,
                brushSelect: false,
                xAxisIndex: dataZoomXAxisIndex,
                textStyle: { color: SecondaryColor },
                handleIcon:
                  "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
                handleSize: "80%",
                filterMode: "none" as const,
                ...(isLarge ? { throttle: 100 } : {}),
              },
            ]
          : []),
        ...(enableMouseWheelZoom
          ? [
              {
                type: "inside",
                start: zoomStart,
                end: zoomEnd,
                xAxisIndex: dataZoomXAxisIndex,
                zoomLock: false,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                preventDefaultMouseMove: false,
                filterMode: "none" as const,
                ...(isLarge ? { throttle: 80 } : {}),
              },
            ]
          : []),
      ],
    };
  }, [
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    brushStart,
    left,
    showBrush,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex?.startIndex,
    brushIndex?.endIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    zoomRange,
    lastZoomRef,
  ]);
}
